import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useMemo, useState } from 'react';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import FormElement from '@/components/FormElement';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import {
  BackfillCandidate,
  BackfillReview,
  PoolRow,
  PreferredPoolsAPI,
  extractPoolsError,
} from '@/services/preferredPoolsAPI';
import { Skill, SkillsAPI } from '@/services/skillsAPI';
import styled from '@/styles';

// "Create new pool" (plan F3, prototype card-header button). Pools are only
// ever created explicitly by ops, per skill (P1/P4); skills that already have
// an active pool are filtered out (the server would 422 on the duplicate —
// edge #17 — and reactivates archived pools itself, edge #18).

const Select = styled('select', {
  width: '100%',
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid $colors$neutralLightest',
  fontSize: '14px',
});

type Props = {
  customerId: number | string;
  existingSkillIds: number[];
  onClose: () => void;
  onCreated: (pool: PoolRow) => void;
  // GRAVY-10441: fired on close when at least one backfill candidate was
  // added. onCreated runs while the pool is still empty, so the parent needs
  // this second signal to reload the member list.
  onSuccess: () => void;
};

const ReviewRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '10px 0',
  borderBottom: '1px solid #EAEAEA',
  '&:last-child': { borderBottom: 'none' },
});

const ReviewMeta = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0,
});

const ReviewName = styled('span', {
  fontSize: '14px',
  fontWeight: 700,
  color: '#1C1C1C',
});

const ReviewStats = styled('span', {
  fontSize: '12px',
  color: '#6B7280',
});

const CreatePoolModal = ({
  customerId,
  existingSkillIds,
  onClose,
  onCreated,
  onSuccess,
}: Props) => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [skillId, setSkillId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // GRAVY-10394: the one-time backfill review returned by createPool. While
  // set, the modal shows the review moment instead of closing; ignoring the
  // candidates (Done / close) creates nothing and never resurfaces.
  const [review, setReview] = useState<{
    pool: PoolRow;
    data: BackfillReview;
  } | null>(null);
  const [addedIds, setAddedIds] = useState<number[]>([]);
  const [addingId, setAddingId] = useState<number | null>(null);

  useEffect(() => {
    SkillsAPI.getSkills()
      .then((response) => setSkills(response.data))
      .catch(() => setError('Failed to load skills'));
  }, []);

  // GRAVY-10367: the skills API is tenant-scoped, so duplicates should not
  // reach here any more; blank and repeated names are still dropped (first
  // occurrence wins) so a regression can never offer "Skill 1" six times.
  const availableSkills = useMemo(() => {
    const seen = new Set<string>();
    return skills.filter((skill) => {
      if (existingSkillIds.includes(skill.id)) return false;
      const name = (skill.name ?? '').trim();
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });
  }, [skills, existingSkillIds]);

  const handleSubmit = async () => {
    if (!skillId) return;
    setLoading(true);
    setError(null);
    try {
      const pool = await PreferredPoolsAPI.createPool(customerId, {
        skill_id: Number(skillId),
      });
      // The parent learns about the pool immediately (so an X-close during
      // the review can't orphan it); only closing waits on the review.
      onCreated(pool);
      const candidates = pool.backfill_review?.candidates ?? [];
      if (pool.backfill_review && candidates.length > 0) {
        setReview({ pool, data: pool.backfill_review });
      } else {
        onClose();
      }
    } catch (err: unknown) {
      setError(extractPoolsError(err, 'Failed to create pool'));
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (candidate: BackfillCandidate) => {
    if (!review) return;
    setAddingId(candidate.worker.id);
    setError(null);
    try {
      await PreferredPoolsAPI.addMember(review.pool.id, {
        worker_id: candidate.worker.id,
        source: 'backfill_review',
      });
      setAddedIds((prev) => [...prev, candidate.worker.id]);
    } catch (err: unknown) {
      setError(extractPoolsError(err, 'Failed to add worker to the pool'));
    } finally {
      setAddingId(null);
    }
  };

  const handleReviewClose = () => {
    if (addedIds.length > 0) onSuccess();
    onClose();
  };

  if (review) {
    const count = review.data.candidates.length;
    return (
      <Modal
        disableClickout
        size="sm"
        title="Create new pool"
        onRequestClose={handleReviewClose}
      >
        <Card.Section>
          {error && (
            <Alert
              description={error}
              icon={faExclamationTriangle}
              status="danger"
              title="Error"
            />
          )}
          <p style={{ fontSize: '14px', marginBottom: '12px' }}>
            {`${count} worker${count === 1 ? '' : 's'} already ${
              count === 1 ? 'has' : 'have'
            } a qualifying 5★ rating for this skill — nominate any of them into the new pool?`}
          </p>
          {review.data.candidates.map((candidate) => {
            const added = addedIds.includes(candidate.worker.id);
            return (
              <ReviewRow key={candidate.worker.id}>
                <ReviewMeta>
                  <ReviewName>{candidate.worker.name}</ReviewName>
                  <ReviewStats>
                    {`${candidate.qualifying_rating_count} × 5★`}
                    {candidate.last_rated_at
                      ? ` · last rated ${new Date(candidate.last_rated_at).toLocaleDateString()}`
                      : ''}
                    {` · ${candidate.shifts_with_client} shifts with client`}
                  </ReviewStats>
                </ReviewMeta>
                <Button
                  a11yLabel={added ? 'Added' : 'Add to pool'}
                  appearance={added ? 'primary' : 'outline'}
                  disabled={added || addingId === candidate.worker.id}
                  isLoading={addingId === candidate.worker.id}
                  label={added ? 'Added' : 'Add to pool'}
                  onClick={() => handleAccept(candidate)}
                />
              </ReviewRow>
            );
          })}
        </Card.Section>
        <Card.Section>
          <Stack justify="end">
            <Button a11yLabel="Done" label="Done" onClick={handleReviewClose} />
          </Stack>
        </Card.Section>
      </Modal>
    );
  }

  return (
    <Modal
      disableClickout
      size="sm"
      title="Create new pool"
      onRequestClose={onClose}
    >
      <Card.Section>
        {error && (
          <Alert
            description={error}
            icon={faExclamationTriangle}
            status="danger"
            title="Error"
          />
        )}
        <FormElement label="Skill">
          <Select
            aria-label="Skill"
            value={skillId}
            onChange={(e) => setSkillId(e.target.value)}
          >
            <option value="">Select a skill...</option>
            {availableSkills.map((skill) => (
              <option key={skill.id} value={skill.id}>
                {skill.name}
              </option>
            ))}
          </Select>
        </FormElement>
      </Card.Section>
      <Card.Section>
        <Stack justify="end">
          <Button
            a11yLabel="Cancel"
            appearance="outline"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            a11yLabel="Create pool"
            disabled={!skillId}
            isLoading={loading}
            label="Create pool"
            onClick={handleSubmit}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default CreatePoolModal;
